import { useGenerationRealtime } from '@/hooks/use-generation-realtime';
import { env } from '@/lib/env';
import { handleError } from '@/lib/error/handle';
import { mutate } from 'swr';
import { toast } from 'sonner';
import { reuseOrAppendHistory } from '@/components/nodes/shared/media-history';

type TranslateFn = (key: string, vars?: Record<string, unknown>) => string;

// Hook 入参类型
export type UseVideoRealtimeParams = {
    // 项目 ID
    projectId?: string | null;
    // 节点 ID
    nodeId: string;
    // 节点数据
    data: any;
    // 模型 ID
    modelId: string;
    // 更新节点数据回调
    updateNodeData: (id: string, partial: any) => void;
    // 获取最新节点数据（避免闭包陈旧数据）
    getNode: (id: string) => any;
    // 翻译函数
    t: TranslateFn;
};

export function useVideoRealtime({ projectId, nodeId, data, modelId, updateNodeData, getNode, t }: UseVideoRealtimeParams) {
    const isRealtimeEnabled =
        String(env.NEXT_PUBLIC_ENABLE_REALTIME ?? 'true') !== 'false' &&
        Boolean(projectId) &&
        (data as any)?.status === 'processing';

    useGenerationRealtime({
        projectId: projectId ?? '',
        nodeId,
        generationId: (data as any)?.processingGenerationId as string | undefined,
        enabled: isRealtimeEnabled,
        onCompleted: (url, metadata) => {
            const nowIso = new Date().toISOString();
            const latestNode = (() => { try { return getNode(nodeId); } catch { return undefined; } })();
            const latestData = (latestNode as any)?.data ?? {};

            const responseMeta = (metadata as any)?.response ?? {};
            const urls: string[] = Array.isArray((responseMeta as any)?.urls)
                ? (responseMeta as any).urls.filter(Boolean)
                : (typeof url === 'string' && url ? [url] : []);

            const { history: nextHistory, activeHistoryId: nextActiveId } = reuseOrAppendHistory(
                latestData?.history,
                urls,
                (id) => ({
                    id,
                    url: urls[0] ?? url,
                    type: 'video/mp4',
                    createdAt: nowIso,
                    prompt: latestData?.instructions ?? '',
                    modelId,
                    aspectRatio: latestData?.aspectRatio,
                    duration: Number(latestData?.duration) || undefined,
                    quality: latestData?.quality,
                    modelUi: latestData?.modelUi,
                    originalUrl: (responseMeta as any)?.originalUrl,
                    ...(urls.length > 1 ? { urls } : {}),
                }),
            );

            const nextGenerated = urls.length > 1
                ? { url: urls[0], urls, type: 'video/mp4' }
                : { url: urls[0] ?? url, type: 'video/mp4' };

            updateNodeData(nodeId, {
                updatedAt: nowIso,
                status: 'completed',
                model: modelId,
                generated: nextGenerated,
                history: nextHistory,
                activeHistoryId: nextActiveId,
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
                originalUrl: (responseMeta as any)?.originalUrl,
                // 样片转正式完成后回归普通正式节点
                draftSource: undefined,
            });
            toast.success(t('Video generated successfully'));
            setTimeout(() => mutate(projectId ? `credits:${projectId}` : 'credits'), 5000);
        },

        onFailed: (error: any) => {
            const nowIso = new Date().toISOString();
            const errorMsg = t(typeof error === 'string' ? error : String(error ?? ''));
            updateNodeData(nodeId, {
                status: 'failed',
                updatedAt: nowIso,
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
                error: errorMsg,
            });
            handleError(t('Video generation failed'), errorMsg);
        }
    });
}
